import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import Fetching from "@/components/Fetching/Fetching";

import { oaConsumptionAvailabilityKey, useConsumeOAProductLine, useOAConsumptionAvailability } from "@/hooks/planning/oa/useOA";

import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";

import OAProductConsumptionLine from "./OAProductConsumptionLine";
import { buildManualCodeSlots, getFilledManualCodes, getManualCodeSlotCount, isUnitMeasurement, normalizeQuantityValue } from "./oa-product-consumption.utils";

import styles from "./modal-oa-product-consumption.module.scss";

const ModalOAProductConsumption = ({ code, onClose }) => {
	const queryClient = useQueryClient();
	const [manualCodesByDetail, setManualCodesByDetail] = useState({});
	const [extraSlotsByDetail, setExtraSlotsByDetail] = useState({});
	const [consumptionModeByDetail, setConsumptionModeByDetail] = useState({});
	const { data, isLoading, isFetching, refetch } = useOAConsumptionAvailability(code, {
		refetchOnMount: "always",
	});
	const { mutate: consumeProductLine, isLoading: isConsuming } = useConsumeOAProductLine();

	useEffect(() => {
		if (!code) {
			return undefined;
		}

		return () => {
			queryClient.removeQueries(oaConsumptionAvailabilityKey(code));
		};
	}, [code, queryClient]);

	const lines = useMemo(() => data?.lines ?? [], [data?.lines]);
	const consumedLinesCount = useMemo(() => lines.filter((line) => normalizeQuantityValue(line.pending_quantity) <= 0).length, [lines]);

	const syncManualCodeSlots = useCallback(
		(activeLines) => {
			setManualCodesByDetail((previousCodes) => {
				const nextCodes = {};
				activeLines.forEach((line) => {
					const slotCount = getManualCodeSlotCount(line, extraSlotsByDetail[line.detail_id] || 0);
					const existingCodes = previousCodes[line.detail_id] || [];
					nextCodes[line.detail_id] = Array.from({ length: slotCount }, (_, index) => existingCodes[index] || "");
				});
				return nextCodes;
			});
		},
		[extraSlotsByDetail],
	);

	useEffect(() => {
		const activeLines = lines.filter((line) => normalizeQuantityValue(line.pending_quantity) > 0);
		if (activeLines.length > 0) {
			syncManualCodeSlots(activeLines);
		}
	}, [lines, syncManualCodeSlots]);

	const handleManualCodeChange = useCallback((detailId, slotIndex, value) => {
		setManualCodesByDetail((previousCodes) => {
			const currentCodes = [...(previousCodes[detailId] || [])];
			currentCodes[slotIndex] = value;
			return {
				...previousCodes,
				[detailId]: currentCodes,
			};
		});
	}, []);

	const handleAddSlot = useCallback((detailId) => {
		setExtraSlotsByDetail((previousSlots) => ({
			...previousSlots,
			[detailId]: (previousSlots[detailId] || 0) + 1,
		}));
	}, []);

	const handleModeChange = useCallback((detailId, mode) => {
		setConsumptionModeByDetail((previousModes) => ({
			...previousModes,
			[detailId]: mode,
		}));
	}, []);

	const clearLineManualCodes = useCallback((detailId) => {
		setManualCodesByDetail((previousCodes) => {
			const nextCodes = { ...previousCodes };
			delete nextCodes[detailId];
			return nextCodes;
		});
		setExtraSlotsByDetail((previousSlots) => {
			const nextSlots = { ...previousSlots };
			delete nextSlots[detailId];
			return nextSlots;
		});
		setConsumptionModeByDetail((previousModes) => {
			const nextModes = { ...previousModes };
			delete nextModes[detailId];
			return nextModes;
		});
	}, []);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleConsumeLine = useCallback(
		(line, { mode = "manual" } = {}) => {
			const manualCodes = getFilledManualCodes(manualCodesByDetail[line.detail_id] || []);
			const isUnitProduct = isUnitMeasurement(line.unit_measurement);
			const requiredSlots = getManualCodeSlotCount(line, extraSlotsByDetail[line.detail_id] || 0);

			if (mode === "manual") {
				if (manualCodes.length === 0) {
					toast.error("Debes ingresar al menos un código.");
					return;
				}

				if (isUnitProduct && manualCodes.length < requiredSlots) {
					toast.error(`Debes ingresar los ${requiredSlots} códigos pendientes.`);
					return;
				}
			}

			consumeProductLine(
				{
					oa_id: code,
					detail_id: line.detail_id,
					inventory_codes: mode === "manual" ? manualCodes : undefined,
				},
				{
					onSuccess: () => {
						if (mode === "auto") {
							toast.success("Producto consumido correctamente.");
						} else if (manualCodes.length > 1) {
							toast.success(`Se dieron de baja ${manualCodes.length} códigos correctamente.`);
						} else {
							toast.success(`Se dio de baja el código ${manualCodes[0]}.`);
						}
						clearLineManualCodes(line.detail_id);
					},
					onError: (mutationError) => {
						const apiMessages = mutationError?.response?.data?.messages;
						const serverMessage =
							Array.isArray(apiMessages) && apiMessages.length > 0 ? apiMessages.join("\n") : mutationError?.response?.data?.error;
						toast.error(serverMessage || "No se pudo consumir el producto.");
					},
				},
			);
		},
		[clearLineManualCodes, code, consumeProductLine, extraSlotsByDetail, manualCodesByDetail],
	);

	const handleConsumeManual = useCallback((line) => handleConsumeLine(line), [handleConsumeLine]);

	const handleConsumeAuto = useCallback((line) => handleConsumeLine(line, { mode: "auto" }), [handleConsumeLine]);

	if (!code) {
		return null;
	}

	const stateVariant = getApplicationOrderStateVariant(Number(data?.state_id));

	return (
		<>
			<CustomProcessingModal open={isConsuming} message="Procesando consumo de productos..." />

			<CustomModalForm
				open={Boolean(code)}
				onClose={handleClose}
				title={`Consumir productos OA ${data?.code || ""}`}
				size="xxl"
				closeOnBackdrop={false}
				closeOnEsc={false}
				showSave={false}
				footer={
					<>
						<CustomButton
							variant="secondary"
							startIcon={<RefreshCw size={16} />}
							onClick={() => refetch()}
							loading={isFetching && !isLoading}
							disabled={isConsuming}
						>
							Actualizar
						</CustomButton>
						<CustomButton variant="secondary" startIcon={<X size={16} />} onClick={handleClose} disabled={isConsuming}>
							Cerrar
						</CustomButton>
					</>
				}
			>
				{isLoading ? (
					<Fetching newStyles={{ marginTop: "2em", marginBottom: "2em" }} />
				) : (
					<div className={styles.content}>
						<div className={styles.summaryBar}>
							<div className={styles.summaryItem}>
								<span>Estado</span>
								<CustomBadge variant={stateVariant} size="sm">
									{data?.state_name || "Sin información"}
								</CustomBadge>
							</div>
							<div className={styles.summaryItem}>
								<span>Bodega OA</span>
								<strong>{data?.estate_name || "Sin información"}</strong>
							</div>
							{lines.length > 0 ? (
								<div className={styles.summaryItem}>
									<span>Progreso</span>
									<strong>
										{consumedLinesCount} de {lines.length} consumido{lines.length === 1 ? "" : "s"}
									</strong>
								</div>
							) : null}
						</div>

						{lines.length === 0 ? (
							<div className={styles.emptyState}>No hay productos planificados para consumir.</div>
						) : (
							<div className={styles.lines}>
								{lines.map((line) => (
									<OAProductConsumptionLine
										key={line.detail_id}
										line={line}
										consumptionMode={consumptionModeByDetail[line.detail_id]}
										onModeChange={handleModeChange}
										manualCodes={
											manualCodesByDetail[line.detail_id] || buildManualCodeSlots(line, [], extraSlotsByDetail[line.detail_id] || 0)
										}
										extraSlots={extraSlotsByDetail[line.detail_id] || 0}
										onManualCodeChange={handleManualCodeChange}
										onAddSlot={handleAddSlot}
										onConsumeManual={handleConsumeManual}
										onConsumeAuto={handleConsumeAuto}
										isConsuming={isConsuming}
									/>
								))}
							</div>
						)}
					</div>
				)}
			</CustomModalForm>
		</>
	);
};

export default ModalOAProductConsumption;
